/**
 * useShellTestCalls - `useTestCalls`, with the shell's roster, asks and log,
 * and where a secret result goes.
 *
 * A secret result is written as a vox line addressed to this seat alone
 * (`secretNote`): the one audience the rules guarantee nobody else reads.
 * Outside a session there is nobody to keep it from, and no vox: it goes
 * to the local log, marked.
 *
 * @param {object} args - roster, asks, actingBy, actedBy, voxPeople, voxMe,
 *   sessionId, isGm, addRollNarrative, unnaturalDosMode, assistBonus, story
 *   (the clocks and the board a result may fill or reveal).
 * @returns {{ testCalls: object, postSecret: (text: string) => void, dispositions: object }}
 */
import { useCallback, useMemo, useRef } from 'react';
import { ridersOf } from '../engine/narrative/callForTest';
import * as multiplayer from '../multiplayer';
import { toDoc } from '../engine/chat';
import { secretNote } from '../engine/narrative/testAsk';
import { diag } from '../diagnostics/log';
import useTestCalls from './useTestCalls';
import useDispositionProposals from './useDispositionProposals';

/**
 * What a rolled result does beyond the log (`ridersOf`): an extended test
 * fills its clock by the degrees it passed with, and a pass shows the board
 * entry it was to reveal. And a social test against somebody proposes a
 * step for their disposition (`useDispositionProposals`).
 */
function useRiders(story, consider) {
  const live = useRef(story);
  live.current = story;
  return useCallback(
    (call, results) => {
      // ONE write per clock and per entry, for results that arrived together: the clock is read
      // from this render, so two fills in a row would each start from the same count - and a clock
      // that marks time would pass it twice over the same roster.
      const riders = results.map((result) => ridersOf(call, result));
      const fill = riders.reduce((sum, r) => sum + (r.fill?.by ?? 0), 0);
      if (fill > 0) live.current.clocks.advance(call.fills, fill);
      const reveal = riders.find((r) => r.reveal)?.reveal;
      if (reveal) live.current.board.edit(reveal, { visible: true });
      // A social test against somebody also suggests how they now stand - see the hook.
      for (const result of results) consider(call, result);
    },
    [consider],
  );
}

export default function useShellTestCalls(args) {
  const { roster, asks, actingBy, actedBy, voxPeople, voxMe, sessionId, isGm, story } = args;
  const { addRollNarrative, unnaturalDosMode, assistBonus } = args;
  const dispositions = useDispositionProposals(roster, story.cast);
  const onResolved = useRiders(story, dispositions.consider);
  const minted = useRef(0);
  const postSecret = useCallback(
    (text) => {
      const ts = Date.now();
      if (!sessionId || !voxMe) {
        addRollNarrative({ type: 'info', text: `SECRET - ${text}`, timestamp: ts });
        return;
      }
      // A count beside the clock: a group's results land in one millisecond and must not share a line.
      const note = secretNote(
        voxMe,
        text,
        ts,
        `${ts}-${(minted.current++).toString(36)}-${voxMe.uid.slice(0, 8)}-secret`,
      );
      // Said, never thrown, and never with the result in it: the diagnostic log is readable by the table.
      multiplayer
        .addChatMessage(sessionId, note.id, toDoc(note))
        .catch(() => diag.error('story', 'a secret result did not save'));
    },
    [sessionId, voxMe, addRollNarrative],
  );
  const me = useMemo(() => ({ uid: voxMe?.uid ?? null }), [voxMe?.uid]);
  // The roller's name for the credit: the people the vox knows, by account.
  const nameOf = useCallback((uid) => voxPeople?.find((p) => p.uid === uid)?.name ?? null, [voxPeople]);
  const testCalls = useTestCalls({
    ...{ roster, asks, actingBy, actedBy, me, nameOf, sessionId, isGm, mp: multiplayer, addRollNarrative, postSecret },
    ...{ unnaturalDosMode, assistBonus, onResolved },
  });
  // `postSecret` goes out too: Stealth's quiet moves are secret results by the same road.
  return { testCalls, postSecret, dispositions };
}
